import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, extractError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, X, Image as ImageIcon } from "lucide-react";

function resolveUrl(u) {
  if (!u) return null;
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  if (u.startsWith("/api/uploads/")) return `${API_BASE.replace(/\/api$/, "")}${u}`;
  return u;
}

export default function ImageUpload({ value, onChange, label = "Image", testId }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Fichier trop lourd (max 5 Mo)"); return; }
    if (!file.type.startsWith("image/")) { toast.error("Merci de sélectionner une image"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("mbk_token");
      const res = await fetch(`${API_BASE}/uploads`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error("Upload échoué");
      const data = await res.json();
      onChange(data.url);
      toast.success("Image téléchargée");
    } catch (err) {
      toast.error(extractError(err, "Erreur d'upload"));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const preview = resolveUrl(value);

  return (
    <div>
      <input type="file" accept="image/*" ref={inputRef} onChange={handleFile} className="hidden" data-testid={`${testId}-input`} />
      {preview ? (
        <div className="relative border border-gray-200 rounded-xl p-3 bg-gray-50">
          <img src={preview} alt={label} className="max-h-32 w-auto rounded-lg mx-auto object-contain" />
          <div className="mt-3 flex items-center justify-between gap-2">
            <div className="text-xs text-gray-500 truncate">{value}</div>
            <div className="flex gap-1 shrink-0">
              <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()} data-testid={`${testId}-replace`} disabled={uploading}>
                {uploading ? "…" : "Remplacer"}
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => onChange("")} data-testid={`${testId}-remove`} className="text-red-600">
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`${testId}-btn`}
          className="w-full border-2 border-dashed border-gray-300 rounded-xl p-6 flex flex-col items-center justify-center gap-2 hover:border-[#0a1e3f] hover:bg-gray-50 transition-colors"
        >
          {uploading ? (
            <div className="text-sm text-gray-500">Téléchargement…</div>
          ) : (
            <>
              <div className="h-10 w-10 rounded-full bg-[#0a1e3f]/5 flex items-center justify-center">
                <Upload className="h-5 w-5 text-[#0a1e3f]" />
              </div>
              <div className="text-sm font-semibold text-[#0a1e3f]">Télécharger une image</div>
              <div className="text-xs text-gray-400">PNG, JPG · max 5 Mo</div>
            </>
          )}
        </button>
      )}
    </div>
  );
}
